import React, { useState, useRef, WheelEvent, MouseEvent, /*useLayoutEffect,*/ useEffect } from "react";
import { sharedPointMapperRef, sharedRubberCanvas } from "./pointMappings";
import { RubberDataBase } from "../shapes/interactionManager"
import { insertShape, replaceShape } from "../../../store/model";
import { useAppDispatch } from '../../../store/hooks'

interface RubberInputProps<T extends RubberDataBase, S extends GeometricShape> {
    mode: string | undefined,
    shape: S,
    rubberInputInit: (shape: S) => T;
    rubberInputRenderer: (rubberPointData: T, cursorPosition: Point2d) => void;
    rubberInputUpdater: (rubberPointData: T, cursorPosition: Point2d) => T;
    onWheel: (event: WheelEvent<HTMLCanvasElement>) => void;
    onMouseDown: (event: MouseEvent<HTMLCanvasElement>) => void;
    onMouseUp: (event: MouseEvent<HTMLCanvasElement>) => void;
    onMouseMove: (event: MouseEvent<HTMLCanvasElement>) => void;
    callback: (shape: S, rubberPointData: T) => S;
}

const RubberInput = <T extends RubberDataBase, S extends GeometricShape, >({mode, shape, rubberInputInit, rubberInputRenderer, rubberInputUpdater, onWheel, onMouseDown, onMouseUp, onMouseMove, callback} : RubberInputProps<T, S>) => {
    const dispatch = useAppDispatch();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [ rubberPointData, setRubberPointData ] = useState<T|null>(rubberInputInit(shape));
    const [ currentMousePos, setCurrentMousePos ] = useState<Point2d>({ x: 0, y: 0 });
    const [ mouseDownTime, setMouseDownTime ] = useState<number | null>(null);
    const [ mouseDownPos, setMouseDownPos ] = useState<Point2d | null>(null);

    const MAX_TIME_DIFF = 200; // ms
    const MAX_POSITION_DIFF = 5; // px

    useEffect(() => {
        setRubberPointData(rubberInputInit(shape));
    }, [rubberInputInit, shape])

    useEffect(() => {
        const ctx = canvasRef.current?.getContext("2d");
        if (!ctx) {
            console.log("Crytical error, there is no canvas for drawing.");
            return;
        }
        console.log("Canvas for drawing initialized.");
        sharedRubberCanvas.current.setContext(ctx);
    }, [canvasRef])

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context  = canvas.getContext("2d");
        if (!context) return;

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.save();


        // context.arc(100, 100, 50, 0, 2 * Math.PI);
        // context.stroke();

        if (rubberInputRenderer && rubberPointData) {
            rubberInputRenderer(rubberPointData, currentMousePos);
        }
    }, [rubberPointData, currentMousePos, rubberInputRenderer]);

    const getMousePosition = (event: MouseEvent): Point2d | null => {
        if (!canvasRef.current) return null;

        const rect = canvasRef.current.getBoundingClientRect();
        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        };
    };

    const isSamePosition = (p1: Point2d, p2: Point2d): boolean => {
        return (
            Math.abs(p1.x - p2.x) <= MAX_POSITION_DIFF &&
            Math.abs(p1.y - p2.y) <= MAX_POSITION_DIFF
        );
    };

    const handleMouseWheel = (e: WheelEvent<HTMLCanvasElement>) => {
        const posInWorld = sharedPointMapperRef.current.screenToWorld({ x: e.clientX, y: e.clientY });
        setCurrentMousePos(posInWorld);

        onWheel(e);
    };

    const handleMouseDown = (event: MouseEvent<HTMLCanvasElement>) => {
        setMouseDownTime(Date.now());
        setMouseDownPos(getMousePosition(event));

        onMouseDown(event);
    };

    const handleMouseMove = (event: MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context  = canvas.getContext("2d");
        if (!context) return;
        const posInWorld = sharedPointMapperRef.current.screenToWorld({ x: event.clientX, y: event.clientY });
        setCurrentMousePos(posInWorld);

        onMouseMove(event);
    }

    const handleMouseUp = (event: MouseEvent<HTMLCanvasElement>) => {
        if (!mouseDownTime || !mouseDownPos) return;

        const timeDiff = Date.now() - mouseDownTime;
        const mouseUpPos = getMousePosition(event);

        if (mouseUpPos) {
            if (timeDiff <= MAX_TIME_DIFF && rubberPointData && isSamePosition(mouseDownPos, mouseUpPos)) {
                const newData = rubberInputUpdater(rubberPointData, currentMousePos);
                if (newData.finished) {
                    console.log("calling callback");
                    const newshape = callback(shape, rubberPointData);
                    if (mode === "create") {
                        dispatch(insertShape(newshape));
                    }
                    if (mode === "edit") {
                        dispatch(replaceShape(newshape));
                    }
                    setRubberPointData(rubberInputInit(shape));
                }
                else {
                    setRubberPointData(newData);
                }
            } else {
                // Just reset state, continue listening
                setMouseDownTime(null);
                setMouseDownPos(null);
            }
        }

        onMouseUp(event);
    };

    return (
        <canvas
            style={{ position: 'absolute', top: '0', left: "0" }}
            width={window.innerWidth}
            height={window.innerHeight}
            ref={canvasRef}
            onWheel={handleMouseWheel}
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
        >
        </canvas>
    );
};

export default RubberInput;